/**
 * Bulk registration for the package's built-in encoding
 * decoders. Invoked from `RawSheet.hydrate`'s `onReady` hook
 * unless its deps say `encodings: "custom"`.
 *
 * Mirrors `registerBuiltinFacades` — one function, one call
 * site, one registry. Consumers that want to override a
 * built-in (e.g. ship their own `event.v1` for a test
 * fixture) pass `encodings: "custom"` in `hydrate`'s deps
 * and register their own set.
 *
 * The model has three layers — wire (Rust serde), hydrated (the
 * generated `Network.*` parse) and canonical (these types) — and a
 * thin decoder per shape bridges them. See
 * `sheets/.docs/content/rows-wire.md`.
 */

import type { EncodingRegistry } from "../core/encoding-registry.ts";

import { EVENT_V1, eventV1 } from "./event-v1.ts";
import { LOGS_V1, logsV1 } from "./logs-v1.ts";
import { SLOT_V1, slotV1 } from "./slot-v1.ts";
import { TASK_V1, taskV1 } from "./task-v1.ts";
import { TIMEZONE_V1, timezoneV1 } from "./timezone-v1.ts";
import { WEATHER_V1, weatherV1 } from "./weather-v1.ts";

/**
 * Bulk-register every built-in decoder on an `EncodingRegistry`.
 * Works with either a standalone registry (used by
 * `LonaSdk.Client` on lona-js side) or a sheet's embedded
 * registry (`sheet.encodings`).
 */
export function registerBuiltinEncodings(registry: EncodingRegistry): void {
  registry.register(EVENT_V1, eventV1);
  registry.register(TASK_V1, taskV1);
  registry.register(WEATHER_V1, weatherV1);
  registry.register(SLOT_V1, slotV1);
  registry.register(TIMEZONE_V1, timezoneV1);
  registry.register(LOGS_V1, logsV1);
}

export { EVENT_V1, eventV1 } from "./event-v1.ts";
export { LOGS_V1, logsV1 } from "./logs-v1.ts";
export { SLOT_V1, slotV1 } from "./slot-v1.ts";
export { TASK_V1, taskV1 } from "./task-v1.ts";
export { TIMEZONE_V1, timezoneV1 } from "./timezone-v1.ts";
export { WEATHER_V1, weatherV1 } from "./weather-v1.ts";
